/**
 * Profile email verification page.
 *
 * Public page that verifies a profile's email address using a token.
 * No authentication required - the token itself is the authentication.
 */

import { trpcClient } from "@/lib/trpc";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";

const searchSchema = z.object({
  token: z.string().min(1, "Verification token is required"),
});

export const Route = createFileRoute("/profiles/verify-email")({
  validateSearch: searchSchema,
  component: ProfileEmailVerifyPage,
});

type VerificationState =
  | { status: "verifying" }
  | { status: "success"; profileName: string; email: string }
  | { status: "error"; message: string };

function ProfileEmailVerifyPage() {
  const { token } = Route.useSearch();
  const [state, setState] = useState<VerificationState>({
    status: "verifying",
  });
  const hasVerifiedRef = useRef(false);

  useEffect(() => {
    if (hasVerifiedRef.current) return;
    hasVerifiedRef.current = true;

    const verifyEmail = async () => {
      try {
        const result = await trpcClient.profile.verifyEmail.mutate({ token });
        setState({
          status: "success",
          profileName: result.profileName,
          email: result.email ?? "",
        });
      } catch (err) {
        setState({
          status: "error",
          message: (err as Error)?.message || "Failed to verify email",
        });
      }
    };

    verifyEmail();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            {state.status === "verifying" && (
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
            )}
            {state.status === "success" && (
              <CheckCircle className="h-12 w-12 text-green-500" />
            )}
            {state.status === "error" && (
              <AlertCircle className="h-12 w-12 text-destructive" />
            )}
          </div>
          <CardTitle>
            {state.status === "verifying" && "Verifying Email..."}
            {state.status === "success" && "Email Verified!"}
            {state.status === "error" && "Verification Failed"}
          </CardTitle>
          <CardDescription>
            {state.status === "verifying" &&
              "Please wait while we verify your email address."}
            {state.status === "success" && (
              <>
                The email address for <strong>{state.profileName}</strong> has
                been verified successfully.
              </>
            )}
            {state.status === "error" && state.message}
          </CardDescription>
        </CardHeader>
        {(state.status === "success" || state.status === "error") && (
          <CardContent className="text-center">
            <Link to="/" className="text-primary hover:underline">
              Go to home
            </Link>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
